/**
 * Detects CRM events that can change the current user's effective
 * permissions.
 *
 * The SPA reads its permission snapshot from `currentOrg.permissions`
 * (TeamSerializer), which is only refreshed by `loadTeams()`. Anything that
 * mutates the user → role → permission-group graph on the backend therefore
 * leaves the UI gated on stale data until something triggers a refresh.
 *
 * The canonical case: creating a custom object type. The backend grants
 * org-wide access to the new type in a Celery post-commit callback
 * (`CustomObjectAccessCallback` → `org_custom_object_access` group attached
 * to every `ahoy_role`). The wizard's immediate `refreshCurrentOrg()` races
 * that callback and usually loses by ~100ms, so Add / Edit / Delete stay
 * disabled until a page reload. The grant itself is committed as ordinary
 * CRM events (`ahoy_permission_group` created / updated, each `ahoy_role`
 * updated) and broadcast over the CRM WebSocket like any other event — so
 * the WebSocket is the reliable signal that the snapshot is now stale.
 */

import type { CrmWebSocketEvent } from './types';

/**
 * Entity types whose events (any event type) change what the user → role →
 * group walk resolves to. Both are readable by every org member
 * (`ahoy_default` grants unrestricted read on each), so the consumer delivers
 * their events without a `subscribe` message.
 */
const PERMISSION_ENTITY_TYPES: ReadonlySet<string> = new Set([
  'ahoy_role',
  'ahoy_permission_group',
]);

/**
 * Association whose add/remove changes which roles a user holds. The event's
 * `entity_type_name` is the user (source side), so it isn't caught by the
 * entity-type check above.
 */
const ROLE_MEMBERSHIP_ASSOCIATION_TYPE = 'ahoy_user_to_role';

function isRoleMembershipChange(event: CrmWebSocketEvent): boolean {
  if (
    event.event_type_name !== 'ahoy_association_added' &&
    event.event_type_name !== 'ahoy_association_removed'
  ) {
    return false;
  }
  return (
    event.payload?.association_type_name === ROLE_MEMBERSHIP_ASSOCIATION_TYPE
  );
}

/**
 * True when at least one event in the batch can change the current user's
 * resolved permissions, i.e. when `currentOrg.permissions` should be
 * refetched.
 */
export function affectsCurrentUserPermissions(
  events: readonly CrmWebSocketEvent[]
): boolean {
  return events.some(
    event =>
      PERMISSION_ENTITY_TYPES.has(event.entity_type_name) ||
      isRoleMembershipChange(event)
  );
}
